import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import i18n from '../i18n/config';
import { getDeviceFingerprint, getDeviceFpHeader } from './deviceFingerprint';
import { getDeviceId } from '../lib/deviceId';

// Automatically detect the API URL based on current host
const getApiUrl = () => {
    // If VITE_API_URL is explicitly set, use it
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }

    // Default to relative path to use Vite proxy or same-origin
    return '';
};

const API_URL = getApiUrl();

// Create axios instance
const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add token and handle content type
apiClient.interceptors.request.use(
    async (config) => {
        const token = useAuthStore.getState().token;

        // Attach the device fingerprints so the backend scopes any abuse block to THIS
        // real device (not a coarse UA hash shared by many CGNAT users):
        //   X-Device-Fp          — hardware/OS payload for FUZZY cross-browser/IP matching
        //   X-Device-Fingerprint — legacy unique uuid+visitorId, precise fallback
        //   X-Device-Id          — stable localStorage+cookie uuid; PRIMARY key for the
        //                          login device list (session dedupe), NOT abuse blocking
        try {
            const [hardwareFp, uuidFp] = await Promise.all([
                getDeviceFpHeader(),
                getDeviceFingerprint(),
            ]);
            if (hardwareFp) config.headers['X-Device-Fp'] = hardwareFp;
            config.headers['X-Device-Fingerprint'] = uuidFp;
        } catch {
            // Never let fingerprinting block a request.
        }
        try {
            config.headers['X-Device-Id'] = getDeviceId();
        } catch {
            // Never let the device id block a request.
        }

        // Define public endpoints that don't need authentication
        const publicEndpoints = [
            '/auth/login',
            '/auth/register',
            '/auth/send-otp',
            '/auth/verify-otp',
            '/auth/forgot-password',
            '/auth/reset-password'
        ];

        // Check if current URL matches any public endpoint
        const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));

        if (token && !isPublicEndpoint) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Don't override Content-Type for FormData
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        if (error.response) {
            // Handle 401 Unauthorized
            if (error.response.status === 401) {
                const currentPath = window.location.pathname;
                const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
                const url = error.config?.url || '';
                const token = useAuthStore.getState().token;
                const isAuthEndpoint =
                    url.includes('/auth/login') ||
                    url.includes('/auth/register') ||
                    url.includes('/auth/send-otp') ||
                    url.includes('/auth/verify-otp') ||
                    url.includes('/auth/forgot-password') ||
                    url.includes('/auth/reset-password') ||
                    url.includes('/auth/change-password') ||
                    url.includes('/auth/account');

                // Only force-redirect if user had a token (i.e., session) and hit a protected endpoint
                // Never redirect on public pages like mobile-upload or payment result pages
                const isPaymentPage = currentPath.startsWith('/payment/');
                if (token && !isAuthEndpoint && currentPath !== '/login' && !currentPath.startsWith('/mobile-upload') && !isPaymentPage) {
                    useAuthStore.getState().logout();
                    window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
                    return Promise.reject(new Error(i18n.t('errors.sessionExpired')));
                }
            }

            // Return error message from server — translate known error codes
            const errorData = error.response.data;
            const errorCode = errorData?.code;
            const errorMessage = errorCode && i18n.exists(`errors.${errorCode}`)
                ? i18n.t(`errors.${errorCode}`, errorData?.data || {})
                : errorData?.message || i18n.t('errors.serverError');
            // Preserve code + data on the Error so callers can branch on specific
            // server codes (e.g. INSUFFICIENT_CREDITS) to surface a dedicated UI
            // instead of a generic toast.
            const err = new Error(errorMessage);
            if (errorCode) err.code = errorCode;
            if (errorData?.data) err.data = errorData.data;
            if (error.response.status) err.status = error.response.status;
            return Promise.reject(err);
        }

        // Network error
        return Promise.reject(new Error(i18n.t('errors.networkError')));
    }
);

export default apiClient;
